import React from "react";
import { Button } from "aible";
import { isScalar, isNonScalarColumn, DatePart } from "types/expression";
import * as Expressions from "types/expression";
import * as ExpressionUtils from "utils/expression";
import { AliasInput, CategoricalValueInput, ColumnValueInput, Labeled, MappedCategoricalValueInput, ScalarValueInput, TextValueInput, ToggleableColumnOrScalarInput } from "./Common";
import { customMapper } from "utils/select";
import { ConditionInput } from "components/UnifiedProject/SelectData/ConditionListInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import styles from "components/Tiles/Sense/style.module.css";
const DEFAULT_CONDITION = ExpressionUtils.andAll([]).build();
const DEFAULT_CASE = Expressions.caseCondition(DEFAULT_CONDITION, undefined);
const CaseConditionInput = ({ index, caseCondition, context, onChange, onDelete, isDisabled }) => {
    const { condition, value } = caseCondition;
    return (React.createElement("div", { className: styles.customRule, key: index },
        React.createElement("div", { className: styles.customRuleHeader }, "Rule " + (index + 1)),
        React.createElement(ConditionInput, { condition: condition, onChange: (val) => onChange({
                condition: val,
                value: value
            }), context: context, isDisabled: isDisabled }),
        React.createElement("div", { className: styles.cTransformButtons },
            React.createElement("div", null,
                "If the criteria is met the new column will display the value:",
                React.createElement(ToggleableExpressionInput, { expr: value, context: context, onChange: (x) => onChange({
                        condition: condition,
                        value: x
                    }), isDisabled: isDisabled }))),
        React.createElement("div", { className: styles.customRuleButtons },
            React.createElement(Button, { onClick: () => onDelete(), style: { marginRight: "10px", color: "#C03E5A" }, disabled: isDisabled },
                React.createElement(FontAwesomeIcon, { icon: faTrash, style: { marginRight: "5px" } }),
                "Delete Rule"))));
};
export const IfThenInput = ({ context, expr, onChange, isDisabled }) => {
    const { cases, else_value } = expr;
    const handleChange = (update) => {
        onChange({ ...expr, ...update });
    };
    const updateCases = (update) => {
        handleChange({ cases: update });
    };
    const getHandleCaseUpdate = (index) => {
        return (update) => updateCases(cases.map((c, i) => (i == index ? update : c)));
    };
    const getHandleCaseDelete = (index) => {
        return () => updateCases(cases.filter((_, i) => i != index));
    };
    return (React.createElement("div", { className: styles.customTransform, style: { width: "100%" } },
        cases.map((c, i) => (React.createElement(CaseConditionInput, { key: `${i}`, index: i, caseCondition: c, context: context, onChange: getHandleCaseUpdate(i), onDelete: getHandleCaseDelete(i), isDisabled: isDisabled }))),
        React.createElement("div", null,
            React.createElement(Button, { onClick: () => updateCases(cases.concat([DEFAULT_CASE])), disabled: isDisabled },
                React.createElement(FontAwesomeIcon, { icon: faPlus, style: { marginRight: "5px" } }),
                "Add Rule")),
        React.createElement("div", { className: styles.tColumnActions, style: { marginTop: "10px" } }, React.createElement("div", { style: { marginBottom: "10px" } },
            "If no criteria are met, the new column will display the value: ",
            React.createElement(ToggleableExpressionInput, { expr: else_value, context: context, onChange: (x) => handleChange({ else_value: x }), isDisabled: isDisabled })))));
};
const NUMERIC_OPS = ["+", "-", "*", "/"];
function getOpsForOutput(output) {
    switch (output?.type) {
        case "NUMERIC":
            return NUMERIC_OPS;
        default:
            return [];
    }
}
export const BinaryOperationInput = ({ context, expr, onChange, isDisabled }) => {
    const leftOutput = ExpressionUtils.getOutput(context, expr.exprs[0]);
    const allowedOps = getOpsForOutput(leftOutput);
    // TODO: add support for dates
    const defaultOutput = { type: "NUMERIC" };
    const allowsInputsForOp = Expressions.getBinaryOperatorInputs(expr.operator);
    const outputs = expr.exprs.map((e) => ExpressionUtils.getOutput(context, e));
    const isInvalidOp = outputs.some((o) => o !== undefined && allowsInputsForOp.find((t) => t === o?.type) === undefined);
    return (React.createElement("div", null, expr.exprs.map((e, index) => (React.createElement(React.Fragment, { key: index },
        index > 0 && (React.createElement("div", { style: { marginTop: "5px" } },
            React.createElement(Labeled, { label: "Operation" },
                React.createElement(CategoricalValueInput, { value: expr.operator, onChange: (operator) => onChange({
                        ...expr,
                        operator: operator ?? allowedOps[0]
                    }), values: allowedOps, isDisabled: isDisabled, isInvalid: isInvalidOp, selectOnly: true })))),
        React.createElement("div", { style: { marginTop: "5px" } },
            React.createElement(Labeled, { label: index == 0 ? "First Column" : index == 1 ? "Second Column" : `Column ${index + 1}}` },
                React.createElement(ExpressionInput, { expr: e, context: context, onChange: (update) => onChange({
                        ...expr,
                        exprs: expr.exprs.map((x, i) => (i == index ? (update ?? Expressions.column("")) : x))
                    }), isDisabled: isDisabled, output: index > 0 ? leftOutput : defaultOutput }))))))));
};
const TYPE_MAPPER = customMapper(ExpressionUtils.humanizeType);
export const CastInput = ({ context, expr, onChange, isDisabled }) => {
    const currentOutput = ExpressionUtils.getOutput(context, expr);
    const output = currentOutput?.type == "NULL" ? undefined : currentOutput;
    const targets = ExpressionUtils.getValidCastTargets(output).filter((t) => t != "NULL");
    return (React.createElement("div", { style: { display: "flex" } },
        React.createElement("div", null,
            React.createElement(Labeled, { label: "Column" },
                React.createElement(ExpressionInput, { expr: expr.expr ?? Expressions.column(""), context: context, onChange: (update) => onChange(Expressions.cast(update, ExpressionUtils.getOutput(context, update)?.type ?? "NULL")), isDisabled: isDisabled }))),
        React.createElement("div", { style: { marginLeft: "8px" } },
            React.createElement(Labeled, { label: "Type" },
                React.createElement(MappedCategoricalValueInput, { value: expr.new_type, onChange: (new_type) => {
                        if (output?.type !== undefined) {
                            onChange(Expressions.cast(expr.expr, new_type ?? output?.type));
                        }
                    }, values: targets, mapper: TYPE_MAPPER, isDisabled: isDisabled || targets.length === 0, isInvalid: targets.find((t) => t === expr.new_type) === undefined, selectOnly: true })))));
};
const DATE_PART_MAPPER = customMapper(ExpressionUtils.humanizeDatePart);
export const ExtractDatePartInput = ({ context, expr, onChange, isDisabled }) => {
    const parts = Object.values(DatePart);
    const dateContext = ExpressionUtils.getRestrictedContext(context, ["DATE"]);
    const output = ExpressionUtils.getOutput(context, expr.date);
    const isInvalid = output !== undefined && output?.type !== "DATE";
    return (React.createElement("div", { style: { display: "flex" } },
        React.createElement("div", null,
            React.createElement(Labeled, { label: "Column" },
                React.createElement(ExpressionInput, { expr: expr.date, context: dateContext, onChange: (update) => onChange(Expressions.datePart(update, expr.part ?? parts[0])), isDisabled: isDisabled }))),
        React.createElement("div", { style: { marginLeft: "8px" } },
            React.createElement(Labeled, { label: "Date Part" },
                React.createElement(MappedCategoricalValueInput, { value: output === undefined ? undefined : expr.part, onChange: (part) => onChange(Expressions.datePart(expr.date, part ?? parts[0])), values: parts, mapper: DATE_PART_MAPPER, isDisabled: isDisabled || parts.length === 0, isInvalid: isInvalid, selectOnly: true })))));
};
const FUNCTION_MAPPER = customMapper(ExpressionUtils.humanizeFunction);
export const FunctionInput = ({ context, expr, onChange, isDisabled, functionProvider }) => {
    const restrictedContext = ExpressionUtils.getRestrictedContext(context, ["TEXT", "NUMERIC"]);
    const currentOutput = ExpressionUtils.getOutput(restrictedContext, expr.expr);
    const funcs = functionProvider(currentOutput);
    const functionInputs = Expressions.getFunctionInputs(expr.func);
    const functionArguments = Expressions.getFunctionArguments(expr.func) || [];
    const isInvalidFunction = currentOutput !== undefined && functionInputs.find((t) => t === currentOutput?.type) === undefined;
    return (React.createElement("div", { style: { display: "flex", flexWrap: "wrap" } },
        React.createElement("div", null,
            React.createElement(Labeled, { label: "Column" },
                React.createElement(ExpressionInput, { expr: expr.expr, context: restrictedContext, onChange: (update) => {
                        const updatedOutput = ExpressionUtils.getOutput(context, update);
                        const updatedFunc = expr.func === undefined || currentOutput?.type != updatedOutput?.type
                            ? functionProvider(updatedOutput ?? currentOutput)[0]
                            : expr.func;
                        onChange(Expressions.columnExpr(updatedFunc, update));
                    }, isDisabled: isDisabled }))),
        React.createElement("div", { style: { marginLeft: "8px" } },
            React.createElement(Labeled, { label: "Operation" },
                React.createElement(MappedCategoricalValueInput, { value: currentOutput !== undefined && expr.expr !== undefined ? expr.func : undefined, onChange: (operator) => onChange(Expressions.columnExpr(operator ?? funcs[0], expr.expr)), values: funcs, mapper: FUNCTION_MAPPER, isDisabled: isDisabled, isInvalid: isInvalidFunction, selectOnly: true }))),
        functionArguments.map((x) => (React.createElement("div", { style: { marginRight: "8px", marginTop: "8px" } },
            React.createElement(Labeled, { key: x.arg, label: x.label },
                React.createElement(ExpressionInput, { context: context, expr: expr.args?.[x.arg], output: { type: x.type }, onChange: (update) => onChange(Expressions.columnExpr(expr.func, expr.expr, { ...expr.args, [x.arg]: update })) })))))));
};
export const UnaryFunctionInput = ({ context, expr, onChange, output, isDisabled }) => {
    return (React.createElement(FunctionInput, { expr: expr, context: context, onChange: onChange, output: output, isDisabled: isDisabled, functionProvider: ExpressionUtils.getUnaryFunctionsForOutput }));
};
export const ScalarFunctionInput = ({ context, expr, onChange, output, isDisabled }) => {
    return (React.createElement(FunctionInput, { expr: expr, context: context, onChange: onChange, output: output, isDisabled: isDisabled, functionProvider: ExpressionUtils.getScalarFunctionsForOutput }));
};
export const AggregationMeasureInput = ({ context, expr, onChange, output, isDisabled, isFilteringDisabled }) => {
    return (React.createElement("div", null,
        React.createElement(ScalarFunctionInput, { expr: expr.expr, context: context, onChange: (update) => onChange(Expressions.aggregateMeasureExpression(update, expr.filter)), output: output, isDisabled: isDisabled }),
        (isFilteringDisabled ?? true) ? (React.createElement(React.Fragment, null)) : (React.createElement("div", null,
            React.createElement(Labeled, { label: "Apply Filter Before Aggregation", style: { marginTop: "10px" } },
                React.createElement("input", { type: "checkbox", style: { marginTop: "5px" }, checked: expr.filter !== undefined, onChange: () => onChange(Expressions.aggregateMeasureExpression(expr.expr, expr.filter !== undefined ? undefined : ExpressionUtils.defaultExpressionFactory().condition())) })),
            expr.filter !== undefined && (React.createElement("div", { style: { marginTop: "10px" } },
                React.createElement(ConditionInput, { condition: expr.filter, onChange: (c) => onChange(Expressions.aggregateMeasureExpression(expr.expr, c)), context: context, isDisabled: isDisabled })))))));
};
export const ToggleableExpressionInput = ({ context, expr, onChange, output, isDisabled }) => {
    if (isScalar(expr) || isNonScalarColumn(expr)) {
        return (React.createElement(ToggleableColumnOrScalarInput, { context: context, value: expr, onChange: onChange, output: output, isDisabled: isDisabled }));
    }
    return React.createElement(ExpressionInput, { expr: expr, context: context, onChange: onChange, output: output, isDisabled: isDisabled });
};
export const ExpressionInput = ({ context, expr, onChange, output, isDisabled }) => {
    if (isScalar(expr)) {
        return (React.createElement(ScalarValueInput, { context: context, value: expr, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
    }
    else if (isNonScalarColumn(expr)) {
        return (React.createElement(ColumnValueInput, { context: context, value: expr, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
    }
    switch (expr.type) {
        case "AGGREGATE":
            return (React.createElement(AggregationMeasureInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
        case "BINARY_OPERATION":
            return (React.createElement(BinaryOperationInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
        case "IF_THEN":
            return (React.createElement(IfThenInput, { context: context, expr: expr, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
        case "CAST":
            return (React.createElement(CastInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
        case "DATE_PART":
            return (React.createElement(ExtractDatePartInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled }));
        case "FUNCTION":
            return (React.createElement(FunctionInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled, functionProvider: ExpressionUtils.isUnaryFunction(expr.func)
                    ? ExpressionUtils.getUnaryFunctionsForOutput
                    : ExpressionUtils.isScalarFunction(expr.func)
                        ? ExpressionUtils.getScalarFunctionsForOutput
                        : (_) => [] }));
    }
    return (React.createElement(ConditionInput, { condition: expr, onChange: (c) => onChange(c), context: context, isDisabled: isDisabled }));
};
export const ExcludableProjectionInput = ({ context, expr, output, onChange, isDisabled, aliasFormatter, isExcluded, onExcludedChange }) => {
    return (React.createElement("div", { style: { display: "flex", alignItems: "center" } },
        isExcluded !== undefined && onExcludedChange !== undefined && (React.createElement("div", { className: styles.jFlexItem, style: { marginRight: "5px", width: "30px", paddingTop: "16px" } },
            React.createElement("input", { type: "checkbox", style: { marginLeft: "8px" }, onChange: () => onExcludedChange(!isExcluded), checked: isExcluded, disabled: isDisabled }))),
        React.createElement(AliasedProjectionInput, { expr: expr, context: context, onChange: onChange, output: output, isDisabled: isDisabled || isExcluded, aliasFormatter: aliasFormatter })));
};
export const BaseProjectionInput = ({ context, expr, onChange, isDisabled, aliasFormatter }) => {
    if (expr === undefined) {
        return React.createElement("div", null);
    }
    const oldAlias = Expressions.getBaseCurrentAlias(expr);
    const newAlias = ExpressionUtils.getAlias(expr);
    const currentOutput = ExpressionUtils.getOutput(context, Expressions.column(oldAlias));
    const targets = ExpressionUtils.getValidCastTargets(currentOutput).filter((t) => t != "NULL");
    const newOutput = ExpressionUtils.getOutput(context, Expressions.projectionToExpression(expr));
    const getExprWithNewAlias = (x) => {
        if (Expressions.isColumnProjection(expr)) {
            if (x === undefined || x == oldAlias) {
                return oldAlias;
            }
            return Expressions.columnProjection(oldAlias, x);
        }
        return ExpressionUtils.builderFrom(expr.expr).buildAliased(x ?? oldAlias);
    };
    const handleAliasChange = (x) => {
        onChange(getExprWithNewAlias(x));
    };
    const getExprWithNewType = (t) => {
        if (t == undefined || t == currentOutput?.type) {
            if (newAlias == oldAlias) {
                return oldAlias;
            }
            return Expressions.columnProjection(oldAlias, newAlias);
        }
        return ExpressionUtils.col(oldAlias).cast(t).buildAliased(newAlias);
    };
    const handleTypeChange = (t) => {
        onChange(getExprWithNewType(t));
    };
    return (React.createElement("div", { className: styles.jFlex },
        React.createElement("div", { className: styles.jFlexItem, style: {
                marginRight: "10px",
                fontSize: "14px",
                maxWidth: "350px"
            } },
            React.createElement(Labeled, { label: oldAlias },
                React.createElement(TextValueInput, { value: newAlias, defaultValue: oldAlias, onChange: (x) => handleAliasChange(aliasFormatter(x)), isDisabled: isDisabled }))),
        React.createElement("div", { className: styles.jFlexItem, style: { maxWidth: "350px" } },
            React.createElement(Labeled, { label: "Type" },
                React.createElement(MappedCategoricalValueInput, { value: newOutput?.type, onChange: handleTypeChange, values: targets, mapper: TYPE_MAPPER, isDisabled: isDisabled || targets.length === 0, selectOnly: true })))));
};
export const AliasedExpressionInput = ({ context, expr, onChange, output, isDisabled, onDelete, aliasFormatter }) => {
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.calcFieldSense },
            React.createElement(ExpressionInput, { expr: expr.expr, context: context, onChange: (update) => onChange(Expressions.aliasedExpression(update, expr.alias ?? "")), output: output, isDisabled: isDisabled })),
        React.createElement("div", { className: styles.aliasInput },
            React.createElement(Labeled, { label: "Alias" },
                React.createElement(AliasInput, { value: expr.alias, valueFormatter: aliasFormatter, onChange: (alias) => {
                        if (expr.expr !== undefined) {
                            onChange(Expressions.aliasedExpression(expr.expr, alias ?? ""));
                        }
                    }, isDisabled: isDisabled || expr.expr === undefined })),
            onDelete && (React.createElement("div", { style: { marginLeft: "5px", display: "flex", alignItems: "end" } },
                React.createElement(Button, { type: "secondary", style: { padding: "7px", height: "38px", width: "38px" }, onClick: () => onDelete(), disabled: isDisabled },
                    React.createElement(FontAwesomeIcon, { icon: faTrash })))))));
};
export const AliasedProjectionInput = ({ context, expr, onChange, output, isDisabled, aliasFormatter, onDelete }) => {
    if (expr === undefined) {
        return React.createElement("div", null);
    }
    else if (Expressions.isBaseProjection(expr)) {
        return (React.createElement(BaseProjectionInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled, aliasFormatter: aliasFormatter }));
    }
    switch (expr.type) {
        case "AGGREGATE":
            return (React.createElement(AliasedExpressionInput, { context: context, expr: Expressions.aliasedExpression(expr, expr.alias), onChange: (e) => onChange(Expressions.aggregateMeasure(e.expr.expr, e.alias, e.expr.filter)), output: output, isDisabled: isDisabled, aliasFormatter: aliasFormatter, onDelete: onDelete }));
        case "EXPRESSION":
            return (React.createElement(AliasedExpressionInput, { expr: expr, context: context, onChange: (e) => onChange(e), output: output, isDisabled: isDisabled, aliasFormatter: aliasFormatter, onDelete: onDelete }));
        case "IF_THEN":
            return (React.createElement(AliasedExpressionInput, { context: context, expr: Expressions.aliasedExpression(expr, expr.alias), onChange: (e) => onChange(Expressions.ifThenWithAlias(e.expr.cases, e.expr.else_value, e.alias)), output: output, isDisabled: isDisabled, aliasFormatter: aliasFormatter, onDelete: onDelete }));
    }
};
export const AliasedProjectionMultiInput = ({ context, exprs, onChange, isDisabled, aliasFormatter, exprProvider }) => {
    return (React.createElement("div", null,
        React.createElement("div", null, exprs.map((expr, index) => (React.createElement("div", { key: index, className: styles.tFlex },
            React.createElement("div", null,
                React.createElement(AliasedProjectionInput, { context: context, expr: expr, onChange: (update) => onChange(exprs.map((x, i) => (i == index ? update : x))), onDelete: () => onChange(exprs.filter((_, i) => i != index)), isDisabled: isDisabled, aliasFormatter: aliasFormatter })))))),
        exprProvider !== undefined && (React.createElement(React.Fragment, null,
            React.createElement("div", { style: {
                    fontSize: "12px",
                    color: "#868595",
                    fontWeight: 300
                } }, "Add New Field"),
            React.createElement("div", null,
                React.createElement(Button, { type: "secondary", onClick: () => onChange(exprs.concat([exprProvider()])), style: { marginRight: "10px" }, disabled: isDisabled },
                    React.createElement(FontAwesomeIcon, { icon: faPlus })))))));
};
